import { MoreDotsIcon } from '@tableau/icons/MoreDotsIcon';
import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { Messages } from '../../../gen-src/Localize';
import { PopupStyle } from '../../styles/Standard';
import { makeTestId } from '../../utils/Declarative';
import { generateId } from '../../utils/DomUtil';
import { PropsAndElementAttrs, RequireLabel, useGlobalPointerDownOutsideListener, useResizeObserver } from '../../utils/ReactHelpers';
import { StackingRootContext } from '../../utils/StackingComponent';
import { MenuButtonProps, MenuButtonWidget } from '../Button/MenuButtonWidget';
import { DropdownOverlay } from '../Dropdown/DropdownOverlay';
import { TextLinkKind, TextLinkProps, TextLinkWidget } from '../TextLinkWidget';
import {
  BreadcrumbDividerStyleName,
  BreadcrumbDividerWidth,
  BreadcrumbMenuButtonStyleName, BreadcrumbsListStyleName,
  BreadcrumbsMenuButtonMinWidth,
  BreadcrumbsDivStyleName,
  BreadcrumbsOverflowListStyleName, BreadcrumbSpacing,
  BreadcrumbStyleName,
  BreadcrumbsNavStyleName,
  TrailingElementsContainerStyleName
} from './BreadcrumbsStyles';

export type BreadcrumbsProps = {
  /**
   * `aria-label` passed to the `nav`. One of `aria-label` or `aria-labelledby` is required.
   */
  'aria-label'?: string;
  /**
   * `aria-labelledby` passed to the `nav`. One of `aria-label` or `aria-labelledby` is required.
   */
  'aria-labelledby'?: string;
  /**
   * The list of `TextLinkWidget` to display. An element (typically the last element) can be a `string` to represent the current page.
   *
   * If the element representing the current page is a `TextLinkWidget` instead of a `string`, put `aria-current='page'` on the
   * `TextLinkWidget` for it to be accessible.
   **/
  children: Array<React.ReactElement<TextLinkProps> | string>;
  /**
   * The index of the first element of `children` to get collapsed into the overflow menu when space is limited.
   * Elements preceding the `collapseStartIndex` will never be collapsed. For e.g., set this value to `1` to allow
   * the 0th element always to be visible.
   *
   * @default ```0```
   */
  collapseStartIndex?: number;
  /**
   * When true, `BreadcrumbsWidget` will use lighter colors.
   * Set to true when using on a dark background.
   */
  darkBackground?: boolean;
  /** Test id to be attached to the root element with suffix `-BreadcrumbsWidget`. */
  testId?: string;
  /**
   * Any elements that should trail behind the breadcrumbs, directly to the right of the last breadcrumb,
   * and should take up some of the space available for the breadcrumbs. These elements are outside of the
   * breadcrumbs `nav` region and will never collapse.
   */
  trailingElements?: Array<React.ReactElement>;
} & RequireLabel;

/** @returns the index of the first breadcrumb that is not collapsed */
function determineVisibleBreadcrumbs(navWidth: number, collapseStartIndex: number, breadcrumbWidths: number[], extraWidths: number[]): number {
  let widthBeforeOverflow = 0;
  for (let i = 0; i < collapseStartIndex; i++) {
    widthBeforeOverflow += breadcrumbWidths[i];
  }
  const extraWidth = extraWidths.reduce((a, b) => a + b, 0);
  const collapsibleSpace = navWidth - widthBeforeOverflow - extraWidth;
  const collapsibleSpaceWithMenu = collapsibleSpace - (BreadcrumbsMenuButtonMinWidth + BreadcrumbDividerWidth + BreadcrumbSpacing * 2);
  let breadcrumbsTotalWidth = 0;
  let firstVisibleBreadcrumb = collapseStartIndex;

  // To determine how many breadcrumbs should be visible, loop through the list of
  // breadcrumbs backwards and compare the length of adding the next breadcrumb to
  // the overall available space.
  for (let i = breadcrumbWidths.length - 1; i >= collapseStartIndex; i--) {
    breadcrumbsTotalWidth += breadcrumbWidths[i];

    // if the first collapsible breadcrumb is not visible, we show the overflow menu button,
    // which takes up some of the available space
    const breadcrumbsGreaterThanAvailableSpace = i === collapseStartIndex ?
      (breadcrumbsTotalWidth > collapsibleSpace) :
      (breadcrumbsTotalWidth > collapsibleSpaceWithMenu);
    if (breadcrumbsGreaterThanAvailableSpace) {
      firstVisibleBreadcrumb = i + 1;
      break;
    }
  }

  return firstVisibleBreadcrumb;
}

/**
 * `BreadcrumbsWidget` is a `nav` region for displaying `TextLinkWidget`s (and/or a `string` to represent the current position) to represent a navigation tree.
 * When there is not enough horizontal space to display all of the breadcrumbs, the breadcrumbs will collapse into an overflow menu.
 */
export const BreadcrumbsWidget: React.FC<PropsAndElementAttrs<BreadcrumbsProps>> = props => {
  const {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    children,
    collapseStartIndex,
    darkBackground,
    testId,
    trailingElements,
    ...breadcrumbsProps
  } = props;
  const [firstVisibleBreadcrumbIndex, setFirstVisibleBreadcrumbIndex] = React.useState<number>(collapseStartIndex!);
  const [navEl, setNavEl] = React.useState<HTMLElement | null>(null);
  const [divEl, setDivEl] = React.useState<HTMLDivElement | null>(null);
  const [updateCount, setUpdateCount] = React.useState(0);
  const breadcrumbWidths = React.useRef<Array<number>>([]);
  const extraWidths = React.useRef<Array<number>>([]);

  if (!(ariaLabel || ariaLabelledby)) {
    throw new Error('At least one of `aria-label` or `aria-labelledby` must be set on `breadcrumbsProps` props.');
  }

  // reset everything any time the list changes
  React.useLayoutEffect(() => {
    setFirstVisibleBreadcrumbIndex(collapseStartIndex!);
    setUpdateCount(count => count + 1); // aka forceUpdate
  }, [children, collapseStartIndex, trailingElements]);

  // determine the width of each breadcrumb before rendering, so that the number of visible breadcrumbs
  // can be calculated based on how many breadcrumbs fit within the width of the container
  React.useLayoutEffect(() => {
    if (!divEl || !navEl) { return; }

    const crumbs = Array.from(navEl.querySelectorAll('li'));
    breadcrumbWidths.current = crumbs.map((crumb, index) => crumb.offsetWidth ?? breadcrumbWidths.current[index]);

    const extras = Array.from(divEl.children).slice(1);
    extraWidths.current = extras.map((extraElement, index) => {
      return extraElement.clientWidth ?? extraWidths.current[index];
    });

    setFirstVisibleBreadcrumbIndex(determineVisibleBreadcrumbs(divEl.clientWidth, collapseStartIndex!, breadcrumbWidths.current, extraWidths.current));
  }, [collapseStartIndex, divEl, navEl, updateCount]);

  const onResize = (entries: ResizeObserverEntry[]) => {
     const navWidth = entries[0].contentRect.width;
    if (navWidth > 0) {
      setFirstVisibleBreadcrumbIndex(determineVisibleBreadcrumbs(navWidth, collapseStartIndex!, breadcrumbWidths.current, extraWidths.current));
    }
  };

  useResizeObserver(divEl, onResize);

  const parentBreadcrumbs = children.slice(0, collapseStartIndex);
  const collapsedBreadcrumbs = children.slice(collapseStartIndex, firstVisibleBreadcrumbIndex);
  const visibleBreadcrumbs = children.slice(firstVisibleBreadcrumbIndex);

  return <div
    className={BreadcrumbsDivStyleName}
    data-tb-test-id={makeTestId(testId, 'BreadcrumbsWidget')}
    ref={setDivEl}
    {...breadcrumbsProps}
  >
    <nav
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      className={BreadcrumbsNavStyleName}
      ref={setNavEl}
    >
      <ol className={BreadcrumbsListStyleName}>
        {(collapseStartIndex! > 0) && parentBreadcrumbs.map((breadcrumb, index) => {
          return <Breadcrumb
            key={index}
            breadcrumb={breadcrumb}
            index={index}
            breadcrumbsLength={children.length}
            darkBackground={darkBackground}
          />;
        })}
        {(firstVisibleBreadcrumbIndex !== collapseStartIndex) &&
          <li style={{ position: 'relative' }} >
            <BreadcrumbsOverflow
              children={collapsedBreadcrumbs}
              kind={darkBackground ? 'outlineDark' : 'outline'}
              testId={testId}
            />
            {firstVisibleBreadcrumbIndex <= children.length - 1 && <BreadcrumbDivider darkBackground={darkBackground} />}
          </li>
        }
        {visibleBreadcrumbs.map((breadcrumb, index) => {
          return <Breadcrumb
            breadcrumb={breadcrumb}
            key={index + firstVisibleBreadcrumbIndex}
            index={index + firstVisibleBreadcrumbIndex}
            breadcrumbsLength={children.length}
            darkBackground={darkBackground}
          />;
        })}
      </ol>
    </nav>
    <div className={TrailingElementsContainerStyleName}>
      {trailingElements}
    </div>
  </div>;
};

BreadcrumbsWidget.displayName = 'BreadcrumbsWidget';
BreadcrumbsWidget.defaultProps = { collapseStartIndex: 0 };

type BreadcrumbDividerProps = {
  /**
   * When true, the divider will be a lighter color.
   */
  darkBackground?: boolean;
};

const BreadcrumbDivider: React.FC<BreadcrumbDividerProps> = props => {
  const { darkBackground } = props;
  return <span aria-hidden className={TypeStyle.classes(BreadcrumbDividerStyleName, { darkBackground })}>{'/'}</span>;
}

BreadcrumbDivider.displayName = 'BreadcrumbDivider';

type BreadcrumbProps = {
  breadcrumb: string | React.ReactElement<TextLinkProps>;
  index: number;
  breadcrumbsLength: number;
  darkBackground?: boolean;
};

const Breadcrumb = React.forwardRef<HTMLLIElement, BreadcrumbProps>((props, breadcrumbRef) => {
  const { breadcrumb, index, breadcrumbsLength, darkBackground } = props;
  let crumb = breadcrumb;
  if (typeof breadcrumb !== 'string') {
    crumb = React.cloneElement<React.ComponentProps<typeof TextLinkWidget>>(breadcrumb, {
      kind: darkBackground ? TextLinkKind.StandaloneDark : TextLinkKind.Standalone,
    });
  }
  return <li
    className={TypeStyle.classes(BreadcrumbStyleName, { darkBackground })}
    key={index}
    ref={breadcrumbRef}
  >
    {crumb}
    {index < breadcrumbsLength - 1 && <BreadcrumbDivider darkBackground={darkBackground} />}
  </li>;
});

Breadcrumb.displayName = 'Breadcrumb';

type BreadcrumbsMenuProps = Pick<BreadcrumbsProps, 'children'> & Pick<MenuButtonProps, 'kind'> & {
  /** Test id to be attached to the `MenuButtonWidget` with suffix `-BreadcrumbsOverflow`. */
  testId?: string;
};

const BreadcrumbsOverflow: React.FC<BreadcrumbsMenuProps> = props => {
  const { children, kind, testId } = props;
  const [isOpen, setOpen] = React.useState(false);
  const [buttonElement, setButtonElement] = React.useState<HTMLButtonElement | null>(null);
  const [overflowElement, setOverflowElement] = React.useState<HTMLDivElement | null>(null);
  const stackingRootRef = React.useRef<HTMLDivElement | null>(null);
  const mouseDownOnButton = React.useRef<boolean>(false);
  const [overflowMenuId] = React.useState<string>(generateId());

  const breadcrumbs = children.map((breadcrumb, index) => {
    let crumb = breadcrumb;
    if (typeof breadcrumb !== 'string') {
      // with `darkBackground`, the `TextLinkWidget`s would be `TextLinkKind.StandaloneDark`
      // but need to be `TextLinkKind.Standalone` to work with the white background of the overflow menu
      crumb = React.cloneElement<React.ComponentProps<typeof TextLinkWidget>>(breadcrumb, {
        kind: TextLinkKind.Standalone,
      });
    }
    return <li key={index}>{crumb}</li>;
  });

  const handleOverflowClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    // clicking on one of the links in the overflow should close the overflow
    if (target.tagName === 'A') {
      toggleOverflow(false);
    }
  };

  const toggleOverflow = (open: boolean = !isOpen) => {
    if (open === isOpen) { return; }

    setOpen(open);
  };

  const handleBlur = (e: React.FocusEvent<HTMLElement>) => {
    const toElement = e.relatedTarget as HTMLElement;
    if (!toElement || e.currentTarget.contains(toElement)) {
      return;
    }

    if (!mouseDownOnButton.current) {
      toggleOverflow(false);
    }

    mouseDownOnButton.current = false;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
    if (e.key === 'Escape' && !e.repeat) {
      toggleOverflow(false);
      buttonElement?.focus();
      e.preventDefault();
    }
    e.stopPropagation();
  };

  const handleButtonMouseDown = () => {
    mouseDownOnButton.current = true;
  };

  const handleButtonMouseUp = () => {
    mouseDownOnButton.current = false;
  };

  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (e.currentTarget.contains(e.target as HTMLElement)) {
      toggleOverflow();
    }
  };

  useGlobalPointerDownOutsideListener(buttonElement!, overflowElement, () => {
    mouseDownOnButton.current = false;
    toggleOverflow(false);
  });

  return <>
    <MenuButtonWidget
      aria-controls={overflowMenuId}
      aria-haspopup={null!}
      aria-label={Messages.moreBreadcrumbsButton()}
      className={BreadcrumbMenuButtonStyleName}
      children={<MoreDotsIcon aria-hidden size={12} style={{ verticalAlign: 'bottom' }} />}
      density='high'
      isOpen={isOpen}
      kind={kind}
      onClick={handleButtonClick}
      onMouseDown={handleButtonMouseDown}
      onMouseUp={handleButtonMouseUp}
      ref={setButtonElement}
      style={{ minWidth: BreadcrumbsMenuButtonMinWidth }}
      testId={makeTestId(testId, 'BreadcrumbsOverflow')}
    />
    <div style={{ position: 'absolute' }} ref={stackingRootRef} />
    {isOpen &&
      <StackingRootContext.Provider value={stackingRootRef.current}>
        <DropdownOverlay
          alignment='left' // by default, will still switch right if out of space
          getAnchorClientRect={() => buttonElement!.getBoundingClientRect()}
          zIndex={1}
        >
          <div
            id={overflowMenuId}
            onBlur={handleBlur}
            onClick={handleOverflowClick}
            onKeyDown={handleKeyDown}
            ref={setOverflowElement}
            style={PopupStyle}
          >
            <ol aria-label={Messages.moreBreadcrumbs()} className={BreadcrumbsOverflowListStyleName}>
              {breadcrumbs}
            </ol>
          </div>
        </DropdownOverlay>
      </StackingRootContext.Provider>
    }
  </>;
};

BreadcrumbsOverflow.displayName = 'BreadcrumbsOverflow';
